<template>
  <div class="campaign-event-subscribers d-flex flex-column">
    <div class="campaign-event-subscribers--upper d-flex align-items-center">
      <h5>Event Subscribers</h5>
      <div class="campaign-event-subscribers-search">
        <b-input-group class="search-input" :class="isDarkMode ? 'dark' : ''">
          <b-input-group-prepend is-text>
            <b-icon icon="search"></b-icon>
          </b-input-group-prepend>
          <b-form-input
            placeholder=""
            v-model="searchKey"
            @keyup.enter="getEventSubscribers"
          ></b-form-input>
        </b-input-group>
      </div>
    </div>
    <hr />
    <div class="campaign-event-subscribers--lower" ref="campaignEventSubscribersLowerRef">
      <b-overlay
        :show="isLoadingEventSubscribers && !isSilentUpdating"
        rounded="sm"
        style="position: relative"
      >
        <b-table
          class="event-subscribers-table"
          sticky-header
          :items="eventSubscribers"
          :class="isDarkMode ? 'dark' : 'light'"
          :show-empty="eventSubscribers.length == 0"
          :style="{
            'max-height': `${subscriberTableMaxHeight}px`
          }"
        >
          <template #empty>
            <div
              class="d-flex align-items-center justify-content-center px-2 py-2"
              :style="{
                height: `${subscriberTableMaxHeight - 50}px`
              }"
            >
              There are no records to show
            </div>
          </template>
          <template v-slot:cell(number)="data">
            <span>{{ data.value }}</span>
          </template>
          <template v-slot:cell(status)="data">
            <span>{{ data.value }}</span>
          </template>
          <template v-slot:cell(deliveredAt)="data">
            <span>{{ data.value }}</span>
          </template>
        </b-table>
        <div
          ref="campaignEventSubscribersTableActionsRef"
          class="event-subscribers-table-actions d-flex align-items-center justify-content-end"
        >
          <b-button
            pill
            variant="outline-secondary"
            :disabled="offset === 0 || isLoadingEventSubscribers"
            @click="prevPage"
          >
            <b-icon-chevron-left></b-icon-chevron-left>
          </b-button>
          <b-button
            pill
            variant="outline-secondary"
            @click="nextPage"
            :disabled="isLoadingEventSubscribers"
          >
            <b-icon-chevron-right></b-icon-chevron-right>
          </b-button>
        </div>
      </b-overlay>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import { runGraphqlQuery } from "../../graphql/utils";
import { makeCampaignEventSubscribersQuery } from "../../graphql/queries";
import moment from "moment";
import { EventBus } from "@/events/eventBus";
import errorHandler from "@/mixins/errorHandler";

export default {
  name: "CampaignEventSubscribers",
  mixins: [errorHandler],
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      searchKey: "",
      isLoadingEventSubscribers: false,
      isSilentUpdating: false,
      offset: 0,
      perPage: 25,
      eventSubscribers: [],
      subscriberTableMaxHeight: 300,
      fields: [
        { key: "number", sortable: false },
        { key: "status", sortable: false },
        { key: "deliveredAt", sortable: false },
      ],
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["isDarkMode", "viewport"]),
  },
  created() {
    this.getEventSubscribers();

    EventBus.$on("update-campaign-event-detail-subscribers", () => {
      if (!this.isLoadingEventSubscribers) {
        this.getEventSubscribers(true)
      }
    });
  },
  methods: {
    getCapitalized(word) {
      if (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return "-";
    },
    prevPage() {
      if (this.offset > this.perPage) {
        this.offset -= this.perPage;
      } else {
        this.offset = 0;
      }
      this.getEventSubscribers();
    },
    nextPage() {
      this.offset += this.perPage;
      this.getEventSubscribers();
    },
    async getEventSubscribers(isSilent = false) {
      try {
        if (this.fetchAbortController) {
          this.fetchAbortController.abort();
          this.fetchAbortController = null;
        }
        this.fetchAbortController = new AbortController();
        this.isLoadingEventSubscribers = true;
        this.isSilentUpdating = isSilent;
        this.$emit("updating", true);
        const query = makeCampaignEventSubscribersQuery({
          offset: this.offset,
          perPage: this.perPage,
          searchKey: this.searchKey ? this.searchKey.trim() : "",
          eventId: this.eventId,
        });

        const resp = await runGraphqlQuery(query, this.fetchAbortController.signal);
        if (resp.data.data.getCampaignEventSubscribers) {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          this.eventSubscribers = resp.data.data.getCampaignEventSubscribers.map(
            (item) => ({
              number: item.subscriber_number,
              status: this.getCapitalized(item.status),
              deliveredAt: item.delivered_at
                ? moment.utc(Number(item.delivered_at)).tz(tz).format("MMM D, YYYY h:mm a")
                : "-",
            })
          );
          this.adjustTableHeight();
        }
      } catch (err) {
        if (!this.isApiCanceled(err)) {
          console.log(
            "[CampaignEventSubscribers:getEventSubscribers] ERROR: ",
            err
          );
        }
      } finally {
        this.isLoadingEventSubscribers = false;
        this.isSilentUpdating = false
        this.$emit("updating", false);
      }
    },
    adjustTableHeight() {
      if (
        this.$refs.campaignEventSubscribersLowerRef &&
        this.$refs.campaignEventSubscribersLowerRef.clientHeight &&
        this.$refs.campaignEventSubscribersTableActionsRef &&
        this.$refs.campaignEventSubscribersTableActionsRef.clientHeight
      ) {
        this.subscriberTableMaxHeight = this.$refs.campaignEventSubscribersLowerRef.clientHeight;
        this.subscriberTableMaxHeight -= this.$refs.campaignEventSubscribersTableActionsRef.clientHeight;
        this.subscriberTableMaxHeight -= 16;
      }
    }
  },
  watch: {
    viewport: {
      handler() {
        this.adjustTableHeight();
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    EventBus.$off("update-campaign-event-detail-subscribers", this.getEventSubscribers);
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
      this.fetchAbortController = null;
    }
  }
};
</script>

<style lang="scss">
.campaign-event-subscribers {
  width: 100%;
  height: 100%;
  &--upper {
    gap: 1.5rem;

    h5 {
      margin: 0;
      white-space: nowrap;
    }

    .campaign-event-subscribers-search {
      flex: 1 1 auto;

      .search-input {
        width: 100% !important;
        >.input-group-prepend {
          >.input-group-text {
            border-radius: 1.5rem 0 0 1.5rem;
          }
        }
        >input {
          border-radius: 0 1.5rem 1.5rem 0 !important;
        }
        & .form-control {
          font-size: 0.875rem;
        }
        &.dark {
          & input::placeholder {
            color: #b2b9bf !important;
          }
          &::before {
            color: #b2b9bf !important;
          }
        }
      }
    }
  }
  &--lower {
    flex: 1;
    .event-subscribers-table {
      &.dark {
        & thead > tr > th {
          background-color: #44475b !important;
          border-top: 1px solid #f0f1f3;
          border-bottom: 1px solid #f0f1f3;
          color: #b2b9bf !important;
        }
        & tbody > tr > td {
          color: #b2b9bf !important;
        }
      }

      & thead {
        & th {
          position: sticky !important;
        }
      }
      & thead > tr > th {
        background-color: #f9f9fc !important;
        border-top: 1px solid #f0f1f3;
        border-bottom: 1px solid #f0f1f3;
        color: #333843 !important;
        font-size: 0.875rem;
        font-weight: 500;
      }
      & tbody > tr > td {
        color: #667085 !important;
        font-size: 0.875rem;
        font-weight: 400;
      }
    }
    .event-subscribers-table-actions {
      gap: 1rem;
    }
  }
}
</style>
